import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Moment from 'moment';
import { cloneDeep as _cloneDeep } from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import IconButton from '@mui/material/IconButton';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FrownyIcon from '@mui/icons-material/SentimentVeryDissatisfied';

import DateTimePicker from '../../../components/styled/datetimepicker';
import ConfirmationDialog from '../../../components/confirmationdlg';
import Auth from '../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    card: {
        maxWidth: 345,
    },
    primary: {
        color: theme.palette.primary.main,
    },
    secondary: {
        color: theme.palette.secondary.main,
    },
    hideAccordion: {
        '&:before': {
            height: '0px',
        },
    },
    hideSummary: {
        height: '0px',
        minHeight: '0px',
        margin: '0px',
        padding: '0px',
        '&.Mui-expanded': {
            minHeight: '0px',
        }
    },
    linebreak: {
        wordBreak: 'break-word',
        whiteSpace: 'pre',
    },
}));

const emptyNote = {
    id: '',
    // created: Moment(),
    title: '',
    desc: '',
};

const IncidentNotes = (props) => {
    const classes = useStyles();
    const theme = useTheme();

    const [incident, setIncident] = useState(props.incident);
    const [note, setNote] = useState(_cloneDeep(emptyNote));
    const [addNoteOpen, setAddNoteOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [disableAdd, setDisableAdd] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const incidentPerms = Auth.permissions.get('Incidents');
    const [readonly] = useState(!incidentPerms.write);

    const handleToggleAddNote = () => {
        setAddNoteOpen(!addNoteOpen);
    };

    const handleCancelNote = () => {
        handleToggleAddNote();
        setNote(_cloneDeep(emptyNote));
        setIsEdit(false);
    };

    const handleAddNote = note => {
        setDisableAdd(true);
        setAddNoteOpen(false);
        setDisableAdd(false);
        props.onUpdate({ data: { notes: [note] } });
    };

    const handleToggleDelete = () => {
        setDeleteOpen(!deleteOpen);
    };

    const handleOpenDelete = (n) => {
        setDeleteOpen(true);
        setNote(n);
    };

    const handleDeleteNote = () => {
        setNote(_cloneDeep(emptyNote));
        handleToggleDelete();
        props.onRemoveData({ notes: [note] });
    };

    const handleEditNote = (note) => {
        setIsEdit(true);
        setAddNoteOpen(true);
        setNote(note);
    };

    // data
    useEffect(() => {
        setIncident(props.incident);
    }, [props.incident]);

    return (
        <Box mt={2} mb={5}>
            <Box display="flex" justifyContent="flex-end">
                {!readonly && <Box>
                    <Button variant="contained" color="secondary" onClick={handleToggleAddNote}><AddIcon /> Note</Button>
                </Box>}
            </Box>
            <Box>
                <Grid container>
                    <Grid item xs={6}></Grid>
                    <Grid item xs={6}>
                        <NewNote
                            edit={isEdit}
                            note={note}
                            addNoteOpen={addNoteOpen}
                            disableAdd={disableAdd}
                            handleAddNote={handleAddNote}
                            handleCancelNote={handleCancelNote}/>
                    </Grid>
                </Grid>
            </Box>
            {!incident.data.notes.length && <Box mt={2}><Typography align="center">No Notes <FrownyIcon /></Typography></Box>}
            {incident.data.notes.map((v, i) => {
                return (
                    <Paper key={'note-' + i} elevation={3}>
                        <Box mt={2} p={2}>
                            <Box display="flex" justifyContent="space-between">
                                <Box mr={2}>
                                    <Typography className={clsx({
                                        [classes.linebreak]: true,
                                        [classes.primary]: true,
                                    })} variant="h6">{v.title}</Typography>
                                </Box>
                                <Box className={classes.primary}>{Moment(v.created).format(theme.date.long)}</Box>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography className={clsx({
                                    [classes.linebreak]: true,
                                })}>{v.desc}</Typography>
                                {!readonly && <Box display="flex">
                                    <Box ml={1}><IconButton color="secondary" onClick={() => { handleOpenDelete(v) }} size="large"><DeleteIcon /></IconButton></Box>
                                    <Box ml={1}><IconButton color="secondary" onClick={() => { handleEditNote(v) }} size="large"><EditIcon /></IconButton></Box>
                                </Box>}
                            </Box>
                        </Box>
                    </Paper>
                );
            })}
            <ConfirmationDialog
                open={deleteOpen}
                title="Delete Note"
                message={(() => {
                    return (
                        <Box>
                            Are you sure you want to delete the note <span className={classes.secondary}>{note.title}</span>?
                        </Box>
                    );
                })()}
                cancelLabel="Cancel"
                okLabel="Yes"
                onClose={handleToggleDelete}
                onOK={handleDeleteNote}
            />
        </Box>
    );
};

IncidentNotes.propTypes = {
    incident: PropTypes.object.isRequired,
    onUpdate: PropTypes.func,
    onRemoveData: PropTypes.func,
};

const NewNote = props => {
    const classes = useStyles();
    const theme = useTheme();

    const [note, setNote] = useState(_cloneDeep(emptyNote));

    const handleUpdateSummary = (e) => {
        setNote({ ...note, title: e.target.value });
    };

    const handleUpdateDetails = (e) => {
        setNote({ ...note, desc: e.target.value });
    };

    const handleDateChange = date => {
        setNote({ ...note, created: date || Moment() });
    };

    const handleCancelNote = () => {
        setNote(_cloneDeep(emptyNote));
        props.handleCancelNote();
    };

    const handleAddNote = () => {
        props.handleAddNote(note);
        setNote(_cloneDeep(emptyNote));
    };

    // use seed note if provided
    useEffect(() => {
        if (props.note) {
            setNote(current => ({ ...current, ...props.note }));
        }
    }, [props.note]);

    return (
        <Accordion expanded={props.addNoteOpen} className={classes.hideAccordion}>
            <AccordionSummary className={classes.hideSummary}></AccordionSummary>
            <AccordionDetails>
                <Box display="block" width="100%">
                    <Box width="100%">
                        <DateTimePicker
                            ampm={false}
                            label="Date"
                            aria-label="date"
                            value={note.created ? Moment(note.created) : Moment()}
                            onChange={handleDateChange}
                            inputFormat={theme.date.short}
                            disableMaskedInput={true}
                            maxDate={Moment()}
                            showTodayButton={true}/>
                    </Box>
                    <Box mt={2} width="100%">
                        <TextField
                            fullWidth
                            label="Summary"
                            value={note.title}
                            onChange={handleUpdateSummary}
                        />
                    </Box>
                    <Box mt={2} width="100%">
                        <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={4}
                            label="Details"
                            value={note.desc}
                            onChange={handleUpdateDetails}
                        />
                    </Box>
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <Box ml={2}><Button variant="contained" color="secondary" onClick={handleCancelNote}>Cancel</Button></Box>
                        <Box ml={2}>
                            <Button variant="contained" color="primary"
                                onClick={handleAddNote}
                                disabled={props.disableAdd || (note.title || '').trim().length === 0}>
                                {props.edit ? 'Update' : 'Create'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
NewNote.propTypes = {
    note: PropTypes.object,
    addNoteOpen: PropTypes.bool,
    disableAdd: PropTypes.bool,
    handleAddNote: PropTypes.func.isRequired,
    handleCancelNote: PropTypes.func.isRequired,
    edit: PropTypes.bool,
};

export {
    IncidentNotes as default,
    NewNote,
};
